import type { LevelDefinition, ParsedLevel } from '../data/levels';
import { parseLevel } from '../data/levels';
import { roomIndexAt, type WorldDefinition } from '../data/world';
import { getFlavor } from '../data/flavors';
import { buildDenseFruitWaves, getFruit, type FruitWave } from '../data/fruits';
import {
  beginLaneLock,
  beginLaneSprint,
  createLaneSprint,
  enemyMoveTicks,
  firstLaneSighting,
  nearestPlayer,
  predictIntercept,
  readyLaneSprint,
  tickLaneSprint,
  windLaneSprint,
} from './enemy-behavior';
import { FlipwallSystem, type FlipwallRules } from './flipwalls';
import {
  FRUIT_ACTION_RETRY_TICKS,
  FRUIT_BEHAVIOR_CONCURRENCY_CAP,
  FRUIT_BEHAVIOR_TIMING,
  fruitBehaviorFor,
  initialFruitActionDelay,
} from './fruit-behavior';
import { buildFruitRoutes } from './fruit-route';
import { Grid } from './grid';
import { findPath } from './pathfinding';
import { SeededRandom } from './random';
import { ScoreSystem } from './scoring';
import {
  cellKey,
  directionBetween,
  manhattan,
  sameCell,
  stepCell,
  type Cell,
  type Difficulty,
  type Direction,
  type EnemyKind,
  type EnemyState,
  type FlavorId,
  type FruitActorState,
  type InputFrame,
  type PlayerState,
  type RunResult,
  type SimulationEvent,
  type WorldState,
} from './types';

export interface SimulationOptions {
  players: 1 | 2;
  flavors: readonly [FlavorId, FlavorId];
  difficulty: Difficulty;
  seed?: number;
  invulnerable?: boolean;
  /** Room layout when this level is the connected world rather than one room. */
  world?: WorldDefinition;
}

/** Wind-up before ice starts breaking, so the melt reads as a struck blow. */
const MELT_WINDUP_TICKS = 9;
/** Fixed steps between each block of a melting run coming apart. */
const MELT_TICKS_PER_CELL = 5;
/** Fixed steps a fruit takes to slip out of a growing Frostwall. */
const FRUIT_DODGE_TICKS = 14;

const OPPOSITE_DIRECTION: Readonly<Record<Direction, Direction>> = {
  up: 'down',
  down: 'up',
  left: 'right',
  right: 'left',
};

const EMPTY_INPUT: InputFrame = { direction: null, placeWall: false, shatter: false, special: false };
const TICKS_PER_SECOND = 60;
const PLAYER_MOVE_TICKS: Readonly<Record<Difficulty, number>> = { relaxed: 7, standard: 6, expert: 6 };
/** Player-facing names for the alert toast and its per-kind audio sting. */
const ENEMY_LABELS: Readonly<Record<EnemyKind, string>> = {
  tracer: 'TRACER',
  rammer: 'RAMMER',
  seer: 'SEER',
  wisp: 'WISP',
  scoop: 'CHERRY SCOOP',
};
// Short enough that releasing a direction stops the cat at the next tile, long
// enough to keep an early corner press. Latest-press priority already handles
// turns made while another direction is still held.
const DIRECTION_BUFFER_TICKS = 5;
const MOVE_COMPLETE_THRESHOLD = 0.999_999;

/** Direction of the tile crossing a player is currently committed to. */
function travelDirection(player: PlayerState): Direction | null {
  if (!player.moving) return null;
  const dx = player.target.x - player.from.x;
  const dy = player.target.y - player.from.y;
  if (dx > 0) return 'right';
  if (dx < 0) return 'left';
  if (dy > 0) return 'down';
  if (dy < 0) return 'up';
  return null;
}

export function playerMoveTicks(difficulty: Difficulty, deepSnow = false): number {
  return PLAYER_MOVE_TICKS[difficulty] + (deepSnow ? 2 : 0);
}

export class GameSimulation {
  readonly definition: LevelDefinition;
  readonly level: ParsedLevel;
  readonly grid: Grid;
  readonly players: PlayerState[];
  readonly enemies: EnemyState[];
  readonly flipwalls = new FlipwallSystem();
  readonly scoring = new ScoreSystem();
  readonly events: SimulationEvent[] = [];
  readonly difficulty: Difficulty;
  readonly coresRemaining: Set<string>;
  readonly world: WorldDefinition | null;
  readonly roomCount: number;
  /**
   * Ice waiting to come apart, keyed by cell. Melting is staged rather than
   * instant so the strike lands, then the run breaks block by block outward.
   */
  readonly meltingCells = new Map<string, number>();
  private meltOwner: 1 | 2 = 1;
  readonly fruitWaves: readonly FruitWave[];
  readonly fruitActors: FruitActorState[] = [];

  worldState: WorldState = 'normal';
  tickCount = 0;
  topologyRevision = 0;
  portalOpen = false;
  paused = false;
  finished = false;
  failed = false;
  result: RunResult | null = null;
  extractionStartTick: number | null = null;
  nextShiftTick: number | null;
  shiftWarningActive = false;
  shardAvailable: Cell | null;
  bossPhase: 0 | 1 | 2 | 3 | 4;
  bossRedirects = 0;
  bossOverloads = 0;
  bossPulseTick = 0;
  invulnerable: boolean;
  activeFruitWaveIndex = 0;
  fruitWaveStartTick = 0;
  fruitPickupGraceUntilTick = 0;

  private readonly random: SeededRandom;
  /** Separate stream keeps enemy patrols deterministic without affecting fruit or decoy behavior. */
  private readonly enemyRandom: SeededRandom;
  private readonly fruitRandom: SeededRandom;
  private readonly fruitActionRetryTicks = new Map<number, number>();
  private readonly nearMissCooldown = new Map<1 | 2, number>();
  private previousFlipwallRevision = 0;

  constructor(definition: LevelDefinition, options: SimulationOptions) {
    this.definition = definition;
    this.level = parseLevel(definition);
    this.grid = new Grid(this.level.width, this.level.height);
    this.difficulty = options.difficulty;
    const seed = options.seed ?? definition.id * 8_191;
    this.random = new SeededRandom(seed);
    this.enemyRandom = new SeededRandom(seed ^ 0x0e11_e5a7);
    this.fruitRandom = new SeededRandom(seed ^ 0x0f17_ca75);
    this.invulnerable = options.invulnerable ?? false;
    this.world = options.world ?? null;
    this.roomCount = this.world?.rooms.length ?? 1;
    this.players = [this.createPlayer(1, options.flavors[0], this.level.playerSpawns[0])];
    if (options.players === 2) this.players.push(this.createPlayer(2, options.flavors[1], this.level.playerSpawns[1]));
    this.enemies = this.level.enemies.map((spawn, index) => ({
      id: index + 1,
      kind: spawn.kind,
      cell: { ...spawn.cell },
      from: { ...spawn.cell },
      target: { ...spawn.cell },
      progress: 0,
      moving: false,
      facing: 'left',
      stunnedTicks: 0,
      alertTicks: 36 + index * 4,
      path: [],
      pathGoal: null,
      topologyRevision: -1,
      predicted: null,
      warningTicks: 0,
      sprint: createLaneSprint(spawn.kind),
      homeRoom: this.roomOf(spawn.cell),
    }));
    const fruitRoutes = buildFruitRoutes(
      this.level,
      definition.id,
      definition.fruitOrder.length,
      // One room per wave, so clearing a room is what opens its door.
      this.world ? (cell) => this.roomOf(cell) : undefined,
    );
    this.fruitWaves = buildDenseFruitWaves(fruitRoutes, definition.fruitOrder);
    this.coresRemaining = new Set(this.fruitWaves[0]?.cells.map(cellKey) ?? []);
    this.activateFruitActors(0);
    this.shardAvailable = this.level.shard ? { ...this.level.shard } : null;
    this.nextShiftTick = definition.automaticShiftMs === null ? null : Math.round(definition.automaticShiftMs / (1000 / TICKS_PER_SECOND));
    this.bossPhase = definition.boss ? 1 : 0;
  }

  step(inputs: readonly InputFrame[]): readonly SimulationEvent[] {
    this.events.length = 0;
    if (this.paused || this.finished || this.failed) return this.events;
    this.tickCount += 1;
    this.scoring.tick();
    this.updateShiftSchedule();
    this.flipwalls.expire(this.tickCount);
    this.advanceMelting();
    this.syncFlipwallTopology();
    this.updateFruitActors();

    for (const player of this.players) this.updatePlayer(player, inputs[player.id - 1] ?? EMPTY_INPUT);
    this.collectOccupiedFruitAfterGrace();
    this.updateRevives();
    for (const enemy of this.enemies) this.updateEnemy(enemy);
    this.updateHazards();
    this.updateBoss();
    this.checkExtractionTimer();
    this.updateCooldowns();
    return this.events;
  }

  getWallPreview(playerId: 1 | 2): Cell[] {
    const player = this.players.find((candidate) => candidate.id === playerId);
    if (!player || player.downed || this.flipwalls.ownerLineCount(player.id) > 0) return [];
    return this.buildWallPreview(player);
  }

  triggerShift(label = 'MANUAL SHIFT'): void {
    this.worldState = this.worldState === 'normal' ? 'overdrive' : 'normal';
    this.topologyRevision += 1;
    this.shiftWarningActive = false;
    const interval = this.bossPhase === 3 ? 360 : this.definition.automaticShiftMs === null
      ? null
      : Math.round(this.definition.automaticShiftMs / (1000 / TICKS_PER_SECOND));
    this.nextShiftTick = interval === null ? null : this.tickCount + interval;
    this.events.push({ type: 'shift', label });
  }

  completeObjectives(): void {
    if (this.isHarvestComplete() || this.finished || this.failed) return;
    this.events.length = 0;
    this.coresRemaining.clear();
    this.activeFruitWaveIndex = Math.max(0, this.fruitWaves.length - 1);
    this.fruitActors.length = 0;
    this.fruitActionRetryTicks.clear();
    this.onAllCoresCollected();
  }

  completeActiveFruitWave(): void {
    if (this.isHarvestComplete() || this.finished || this.failed) return;
    this.events.length = 0;
    const activeCells = this.getActiveFruitCells();
    for (const cell of activeCells) this.coresRemaining.delete(cellKey(cell));
    const origin = activeCells.at(-1) ?? this.players[0]?.cell ?? this.level.playerSpawns[0];
    const bonus = this.scoring.completeFruitWave(this.activeFruitWaveIndex);
    if (this.isFinalFruitWave()) {
      this.fruitActors.length = 0;
      this.fruitActionRetryTicks.clear();
      this.events.push({ type: 'final-core', cell: origin, amount: bonus, fruitKind: this.activeFruitWave.kind });
      this.onAllCoresCollected();
    } else {
      this.advanceFruitWave(origin, bonus);
    }
  }

  get activeFruitWave(): FruitWave {
    return this.fruitWaves[this.activeFruitWaveIndex] ?? { kind: 'strawberry', cells: [] };
  }

  getActiveFruitCells(): readonly Cell[] {
    return this.fruitActors.map((actor) => ({ ...actor.cell }));
  }

  getActiveFruitActors(): readonly FruitActorState[] {
    return this.fruitActors;
  }

  getActiveFruitRemainingCount(): number {
    return this.getActiveFruitCells().length;
  }

  getFruitTypesRemainingCount(): number {
    return this.isHarvestComplete() ? 0 : Math.max(0, this.fruitWaves.length - this.activeFruitWaveIndex);
  }

  isHarvestComplete(): boolean {
    return this.fruitWaves.length === 0
      || (this.activeFruitWaveIndex >= this.fruitWaves.length - 1 && this.coresRemaining.size === 0);
  }

  debugFinish(): void {
    if (this.finished || this.failed) return;
    this.events.length = 0;
    this.portalOpen = true;
    this.finishRun();
  }

  debugThawPlayer(playerId: 1 | 2 = 1): void {
    if (this.finished || this.failed) return;
    const player = this.players.find((candidate) => candidate.id === playerId);
    if (!player || player.downed) return;
    this.events.length = 0;
    const wasInvulnerable = this.invulnerable;
    this.invulnerable = false;
    this.damagePlayer(player, 'DEBUG CONTACT');
    this.invulnerable = wasInvulnerable;
  }

  addSecondPlayer(flavor: FlavorId): boolean {
    if (this.players.length > 1 || this.finished || this.failed) return false;
    this.players.push(this.createPlayer(2, flavor, this.level.playerSpawns[1]));
    return true;
  }

  removeSecondPlayer(): boolean {
    // P2 is the only possible rescuer while P1 is thawed. Removing them here
    // would leave a non-failed run with no living player and no recovery path.
    if (this.players.length < 2 || this.players[0]?.downed) return false;
    this.players.splice(1, 1);
    const removedLines = this.flipwalls.shatter(2);
    if (removedLines.cells.length > 0) this.topologyRevision += 1;
    return true;
  }

  getShiftCountdownTicks(): number | null {
    if (this.nextShiftTick === null) return null;
    return Math.max(0, this.nextShiftTick - this.tickCount);
  }

  isGateClosed(): boolean {
    return this.tickCount % 240 < 120;
  }

  isLaserActive(): boolean {
    const cycle = this.tickCount % 180;
    return cycle >= 138 && cycle < 158;
  }

  isLaserWarning(): boolean {
    const cycle = this.tickCount % 180;
    return cycle >= 108 && cycle < 138;
  }

  isFadingFloorActive(): boolean {
    const cycle = this.tickCount % 240;
    return cycle >= 174 && cycle < 210;
  }

  isFadingFloorWarning(): boolean {
    const cycle = this.tickCount % 240;
    return cycle >= 140 && cycle < 174;
  }

  private createPlayer(id: 1 | 2, flavor: FlavorId, spawn: Cell): PlayerState {
    // ICECAT is a one-contact arcade game on every difficulty. Difficulty still
    // changes pursuit and movement pressure, but never how many hits a cat gets.
    const maxHealth = 1;
    const character = getFlavor(flavor).ability;
    return {
      id,
      character,
      flavor,
      cell: { ...spawn },
      from: { ...spawn },
      target: { ...spawn },
      progress: 0,
      facing: id === 1 ? 'right' : 'up',
      buffered: null,
      bufferTicks: 0,
      moving: false,
      health: maxHealth,
      maxHealth,
      invulnerableTicks: 0,
      specialCooldownTicks: 0,
      downed: false,
      reviveTicks: 0,
      decoy: null,
      wallToggleQueued: false,
      wallHeld: false,
    };
  }

  /**
   * Fixed steps this enemy needs to cross one tile. Rendering uses it to
   * interpolate between simulation ticks with the same cadence the rules use,
   * so creatures glide instead of stepping.
   */
  enemyStepTicks(enemy: EnemyState): number {
    return enemyMoveTicks(
      enemy,
      this.worldState,
      this.difficulty,
      this.extractionStartTick !== null,
      (cell) => this.flipwalls.hasCell(cell),
    );
  }

  private updatePlayer(player: PlayerState, input: InputFrame): void {
    if (input.direction) {
      player.buffered = input.direction;
      player.bufferTicks = DIRECTION_BUFFER_TICKS;
      player.facing = input.direction;
    } else if (player.buffered !== null && player.buffered === travelDirection(player)) {
      // Releasing the key must stop the cat at the next tile. The buffer exists
      // to remember an early corner press, not to extend a run, so an intent
      // that only repeats the current heading is dropped the moment it is let
      // go. A genuine queued turn keeps its remaining buffer ticks below.
      player.buffered = null;
      player.bufferTicks = 0;
    } else if (player.bufferTicks > 0) {
      player.bufferTicks -= 1;
      if (player.bufferTicks === 0) player.buffered = null;
    }
    if (player.invulnerableTicks > 0) player.invulnerableTicks -= 1;
    if (player.specialCooldownTicks > 0) player.specialCooldownTicks -= 1;
    this.updateDecoy(player);
    if (player.downed) return;

    // Edge-triggered: one Frostwall action per press. Sampling the key level
    // instead would fire the toggle on every tick the key is down, so a single
    // keypress would build and melt the same wall five to nine times.
    if (input.placeWall) {
      if (!player.wallHeld) player.wallToggleQueued = true;
    } else if (input.shatter) {
      this.shatterWalls(player);
    }
    player.wallHeld = input.placeWall;
    if (input.special) this.useSpecial(player);

    if (player.moving) {
      player.progress += 1 / playerMoveTicks(this.difficulty, this.level.snowFloors.has(cellKey(player.target)));
      if (player.progress >= MOVE_COMPLETE_THRESHOLD) {
        player.cell = { ...player.target };
        player.from = { ...player.cell };
        player.progress = 0;
        player.moving = false;
        this.onPlayerArrived(player);
      }
    }

    if (!player.moving) {
      if (player.wallToggleQueued) {
        player.wallToggleQueued = false;
        this.toggleWalls(player);
      }
      const key = cellKey(player.cell);
      const conveyor = this.level.conveyors.get(key);
      const iceGlide = this.level.iceFloors.has(key) ? player.facing : null;
      const wanted = player.buffered ?? conveyor ?? iceGlide;
      if (wanted && this.tryStartPlayerMove(player, wanted)) {
        player.buffered = null;
        player.bufferTicks = 0;
      }
    }
  }

  private tryStartPlayerMove(player: PlayerState, direction: Direction): boolean {
    const destination = stepCell(player.cell, direction);
    player.facing = direction;
    if (this.isBlockedForPlayer(destination) || this.isPlayerReserved(destination, player.id)) return false;
    player.from = { ...player.cell };
    player.target = { ...destination };
    player.progress = 0;
    player.moving = true;
    this.events.push({ type: 'move', playerId: player.id, cell: destination });
    return true;
  }

  private onPlayerArrived(player: PlayerState): void {
    const key = cellKey(player.cell);
    this.tryCollectFruit(player);
    if (this.shardAvailable && sameCell(this.shardAvailable, player.cell)) {
      this.shardAvailable = null;
      this.scoring.shard();
      this.events.push({ type: 'shard', playerId: player.id, cell: player.cell });
    }
    if (this.level.shiftPads.has(key) && this.nextShiftTick !== null) {
      this.nextShiftTick = Math.max(this.tickCount + 90, this.nextShiftTick - 120);
    }
    if (this.portalOpen && sameCell(player.cell, this.level.exit)) this.finishRun();

    const nearEnemy = this.enemies.some((enemy) => enemy.stunnedTicks === 0 && manhattan(enemy.cell, player.cell) === 1);
    if (nearEnemy && (this.nearMissCooldown.get(player.id) ?? 0) === 0) {
      this.scoring.nearMiss();
      this.nearMissCooldown.set(player.id, 90);
    }
    this.checkEnemyCollision(player);
  }

  private tryCollectFruit(player: PlayerState): void {
    if (this.tickCount < this.fruitPickupGraceUntilTick) return;
    const key = cellKey(player.cell);
    const actorIndex = this.fruitActors.findIndex((actor) => actor.collectible && (
      cellKey(actor.cell) === key
      || ((actor.phase === 'drifting' || actor.phase === 'running') && cellKey(actor.from) === key)
    ));
    if (actorIndex < 0) return;
    const actor = this.fruitActors[actorIndex];
    if (!actor || !this.coresRemaining.delete(cellKey(actor.cell))) return;
    this.fruitActors.splice(actorIndex, 1);
    this.fruitActionRetryTicks.delete(actor.id);
    const collectedKind = this.activeFruitWave.kind;
    const pickupPoints = this.scoring.collectCore();
    const waveComplete = this.getActiveFruitRemainingCount() === 0;
    if (!waveComplete) {
      this.events.push({ type: 'core', playerId: player.id, cell: player.cell, amount: pickupPoints, fruitKind: collectedKind });
      return;
    }
    const waveBonus = this.scoring.completeFruitWave(this.activeFruitWaveIndex);
    if (this.isFinalFruitWave()) {
      this.fruitActors.length = 0;
      this.fruitActionRetryTicks.clear();
      this.events.push({ type: 'final-core', playerId: player.id, cell: player.cell, amount: pickupPoints + waveBonus, fruitKind: collectedKind });
      this.onAllCoresCollected();
      return;
    }
    this.events.push({ type: 'core', playerId: player.id, cell: player.cell, amount: pickupPoints, fruitKind: collectedKind });
    this.advanceFruitWave(player.cell, waveBonus);
  }

  private collectOccupiedFruitAfterGrace(): void {
    if (this.tickCount < this.fruitPickupGraceUntilTick || this.isHarvestComplete()) return;
    const waveIndex = this.activeFruitWaveIndex;
    for (const player of this.players) {
      if (!player.downed) this.tryCollectFruit(player);
      if (this.activeFruitWaveIndex !== waveIndex || this.coresRemaining.size === 0) break;
    }
  }

  private isFinalFruitWave(): boolean {
    return this.activeFruitWaveIndex >= this.fruitWaves.length - 1;
  }

  private advanceFruitWave(origin: Cell, bonus: number): void {
    if (this.activeFruitWaveIndex >= this.fruitWaves.length - 1) return;
    // Queued fruit routes are intentionally not reserved during the current
    // family. Dissolving the full temporary network guarantees that no wall
    // placed for the old route can isolate the newly active one.
    this.flipwalls.clear();
    this.syncFlipwallTopology();
    this.activeFruitWaveIndex += 1;
    this.fruitWaveStartTick = this.tickCount + 1;
    this.fruitPickupGraceUntilTick = this.tickCount + 18;
    this.coresRemaining.clear();
    this.activeFruitWave.cells.forEach((cell) => this.coresRemaining.add(cellKey(cell)));
    this.activateFruitActors(this.fruitWaveStartTick);
    const fruit = getFruit(this.activeFruitWave.kind);
    this.events.push({
      type: 'fruit-wave',
      cell: origin,
      amount: bonus,
      label: fruit.name,
      fruitKind: fruit.id,
    });
  }

  private activateFruitActors(startTick: number): void {
    this.fruitActors.length = 0;
    this.fruitActionRetryTicks.clear();
    const kind = this.activeFruitWave.kind;
    const behavior = fruitBehaviorFor(kind);
    this.activeFruitWave.cells.forEach((cell, index) => {
      this.fruitActors.push({
        id: this.activeFruitWaveIndex * 100 + index + 1,
        kind,
        behavior,
        cell: { ...cell },
        from: { ...cell },
        target: { ...cell },
        facing: index % 2 === 0 ? 'right' : 'left',
        phase: 'idle',
        phaseTicksRemaining: 0,
        phaseDurationTicks: 0,
        progress: 0,
        collectible: true,
        nextActionTick: startTick + initialFruitActionDelay(behavior, index),
        frozen: false,
      });
    });
  }

  private updateFruitActors(): void {
    this.thawFruitWithoutIce();
    for (const actor of this.fruitActors) {
      if (actor.frozen || actor.phase === 'idle') continue;
      switch (actor.phase) {
        case 'drift-warning':
        case 'run-warning':
          if (this.tickFruitPhase(actor)) this.startGroundFruitMove(actor);
          break;
        case 'flight-warning':
          if (this.tickFruitPhase(actor)) this.startPineappleFlight(actor);
          break;
        case 'teleport-warning':
          if (this.tickFruitPhase(actor)) this.startCherryTeleport(actor);
          break;
        case 'drifting':
        case 'running':
          if (this.tickFruitPhase(actor)) this.settleFruit(actor);
          break;
        case 'airborne':
          if (this.tickFruitPhase(actor)) {
            actor.phase = 'landing';
            actor.phaseDurationTicks = FRUIT_BEHAVIOR_TIMING['leaf-flight'].recoveryTicks;
            actor.phaseTicksRemaining = actor.phaseDurationTicks;
            actor.progress = 1;
            actor.collectible = true;
          }
          break;
        case 'landing':
          if (this.tickFruitPhase(actor)) this.settleFruit(actor);
          break;
        case 'teleporting':
          if (this.tickFruitPhase(actor)) this.settleFruit(actor);
          break;
      }
    }

    const activeByBehavior = new Map<FruitActorState['behavior'], number>();
    for (const actor of this.fruitActors) {
      if (actor.frozen || actor.phase === 'idle') continue;
      activeByBehavior.set(actor.behavior, (activeByBehavior.get(actor.behavior) ?? 0) + 1);
    }
    const ready = this.fruitActors
      .filter((actor) => actor.phase === 'idle'
        && !actor.frozen
        && actor.behavior !== 'stationary'
        && this.tickCount >= actor.nextActionTick
        && this.tickCount >= (this.fruitActionRetryTicks.get(actor.id) ?? actor.nextActionTick))
      .sort((left, right) => left.nextActionTick - right.nextActionTick || left.id - right.id);
    for (const actor of ready) {
      const active = activeByBehavior.get(actor.behavior) ?? 0;
      if (active >= FRUIT_BEHAVIOR_CONCURRENCY_CAP[actor.behavior]) {
        this.fruitActionRetryTicks.set(actor.id, this.tickCount + FRUIT_ACTION_RETRY_TICKS);
        continue;
      }
      this.fruitActionRetryTicks.delete(actor.id);
      this.beginFruitWarning(actor);
      activeByBehavior.set(actor.behavior, active + 1);
    }
  }

  private beginFruitWarning(actor: FruitActorState): void {
    const timing = FRUIT_BEHAVIOR_TIMING[actor.behavior];
    if (actor.behavior === 'stationary') return;
    actor.phaseDurationTicks = timing.warningTicks;
    actor.phaseTicksRemaining = timing.warningTicks;
    actor.progress = 0;
    actor.collectible = true;
    if (actor.behavior === 'slow-roam') actor.phase = 'drift-warning';
    else if (actor.behavior === 'runner') actor.phase = 'run-warning';
    else if (actor.behavior === 'leaf-flight') actor.phase = 'flight-warning';
    else actor.phase = 'teleport-warning';
    if (timing.warningTicks <= 0) return;
    const label = actor.behavior === 'runner' ? 'PEAR DASH'
      : actor.behavior === 'leaf-flight' ? 'LEAF ROTOR'
        : 'CHERRY PHASE';
    this.events.push({ type: 'fruit-warning', cell: actor.cell, fruitKind: actor.kind, label });
  }

  private tickFruitPhase(actor: FruitActorState): boolean {
    actor.phaseTicksRemaining = Math.max(0, actor.phaseTicksRemaining - 1);
    actor.progress = actor.phaseDurationTicks <= 0
      ? 1
      : 1 - actor.phaseTicksRemaining / actor.phaseDurationTicks;
    return actor.phaseTicksRemaining === 0;
  }

  private startGroundFruitMove(actor: FruitActorState): void {
    const reachable = this.reachableFruitCells();
    const candidates = this.grid.neighbors(actor.cell, (cell) => !this.isSafeFruitDestination(cell, actor, reachable));
    if (candidates.length === 0) {
      this.deferFruitAction(actor, 30);
      return;
    }
    let choices = candidates;
    if (actor.behavior === 'slow-roam') {
      const ahead = stepCell(actor.cell, actor.facing);
      const behind = stepCell(actor.cell, OPPOSITE_DIRECTION[actor.facing]);
      const straight = candidates.filter((cell) => sameCell(cell, ahead));
      const turning = candidates.filter((cell) => !sameCell(cell, behind));
      // Mostly keep gliding forward; occasionally take a turn so the drift
      // still wanders. Reversing is the last resort at a dead end.
      const wantsTurn = this.fruitRandom.integer(7) === 0;
      choices = (!wantsTurn && straight.length > 0)
        ? straight
        : turning.length > 0 ? turning : candidates;
    }
    if (actor.behavior === 'runner') {
      const distanceFromCats = (cell: Cell): number => Math.min(
        ...this.players.filter((player) => !player.downed).map((player) => manhattan(cell, player.cell)),
      );
      const bestDistance = Math.max(...candidates.map(distanceFromCats));
      choices = candidates.filter((cell) => distanceFromCats(cell) === bestDistance);
    }
    const destination = choices[this.fruitRandom.integer(choices.length)] ?? actor.cell;
    this.reserveFruitDestination(actor, destination);
    const timing = FRUIT_BEHAVIOR_TIMING[actor.behavior];
    actor.phase = actor.behavior === 'runner' ? 'running' : 'drifting';
    actor.phaseDurationTicks = timing.travelTicks;
    actor.phaseTicksRemaining = timing.travelTicks;
    actor.progress = 0;
    actor.collectible = true;
    this.events.push({
      type: 'fruit-move',
      cell: destination,
      fruitKind: actor.kind,
      label: actor.behavior === 'runner' ? 'RUN' : 'DRIFT',
    });
  }

  private startPineappleFlight(actor: FruitActorState): void {
    const destination = this.chooseFruitRelocation(actor, 4);
    if (!destination) {
      this.deferFruitAction(actor, 60);
      return;
    }
    this.reserveFruitDestination(actor, destination);
    const timing = FRUIT_BEHAVIOR_TIMING['leaf-flight'];
    actor.phase = 'airborne';
    actor.phaseDurationTicks = timing.travelTicks;
    actor.phaseTicksRemaining = timing.travelTicks;
    actor.progress = 0;
    actor.collectible = false;
    this.events.push({ type: 'fruit-move', cell: destination, fruitKind: actor.kind, label: 'TAKEOFF' });
  }

  private startCherryTeleport(actor: FruitActorState): void {
    const destination = this.chooseFruitRelocation(actor, 3);
    if (!destination) {
      this.deferFruitAction(actor, 60);
      return;
    }
    this.reserveFruitDestination(actor, destination);
    const timing = FRUIT_BEHAVIOR_TIMING.teleport;
    actor.phase = 'teleporting';
    actor.phaseDurationTicks = timing.travelTicks;
    actor.phaseTicksRemaining = timing.travelTicks;
    actor.progress = 0;
    actor.collectible = false;
    this.events.push({ type: 'fruit-teleport', cell: destination, fruitKind: actor.kind, label: 'PHASE' });
  }

  private settleFruit(actor: FruitActorState): void {
    actor.from = { ...actor.cell };
    actor.target = { ...actor.cell };
    actor.phase = 'idle';
    actor.phaseTicksRemaining = 0;
    actor.phaseDurationTicks = 0;
    actor.progress = 0;
    actor.collectible = true;
    actor.nextActionTick = actor.behavior === 'stationary'
      ? Number.MAX_SAFE_INTEGER
      : this.tickCount + FRUIT_BEHAVIOR_TIMING[actor.behavior].idleTicks;
  }

  private deferFruitAction(actor: FruitActorState, ticks: number): void {
    actor.from = { ...actor.cell };
    actor.target = { ...actor.cell };
    actor.phase = 'idle';
    actor.phaseTicksRemaining = 0;
    actor.phaseDurationTicks = 0;
    actor.progress = 0;
    actor.collectible = true;
    actor.nextActionTick = this.tickCount + ticks;
  }

  private reserveFruitDestination(actor: FruitActorState, destination: Cell): void {
    const oldKey = cellKey(actor.cell);
    const newKey = cellKey(destination);
    this.coresRemaining.delete(oldKey);
    actor.from = { ...actor.cell };
    actor.target = { ...destination };
    actor.cell = { ...destination };
    actor.facing = directionBetween(actor.from, destination);
    this.coresRemaining.add(newKey);
  }

  private chooseFruitRelocation(actor: FruitActorState, preferredDistance: number): Cell | null {
    const reachable = this.reachableFruitCells();
    const candidates: Cell[] = [];
    for (let y = 0; y < this.level.height; y += 1) {
      for (let x = 0; x < this.level.width; x += 1) {
        const cell = { x, y };
        if (this.isSafeFruitDestination(cell, actor, reachable) && !sameCell(cell, actor.cell)) candidates.push(cell);
      }
    }
    if (candidates.length === 0) return null;
    const distant = candidates.filter((cell) => manhattan(cell, actor.cell) >= preferredDistance);
    const choices = distant.length > 0 ? distant : candidates;
    return choices[this.fruitRandom.integer(choices.length)] ?? null;
  }

  private reachableFruitCells(): ReadonlySet<string> {
    const reachable = new Set<string>();
    for (const player of this.players) {
      if (player.downed) continue;
      for (const key of this.grid.flood(player.cell, (cell) => this.isBlockedForPlayer(cell))) reachable.add(key);
    }
    return reachable;
  }

  private isSafeFruitDestination(cell: Cell, actor: FruitActorState, reachable: ReadonlySet<string>): boolean {
    const key = cellKey(cell);
    if (!this.grid.isInside(cell)
      || !reachable.has(key)
      || this.level.permanentWalls.has(key)
      || this.level.breakables.has(key)
      || this.level.gates.has(key)
      || this.level.normalWalls.has(key)
      || this.level.overdriveWalls.has(key)
      || this.level.lasers.has(key)
      || this.level.fadingFloors.has(key)
      || this.flipwalls.hasCell(cell)
      || sameCell(cell, this.level.exit)
      || (this.level.boss !== null && sameCell(cell, this.level.boss))
      || (this.shardAvailable !== null && sameCell(cell, this.shardAvailable))) return false;
    if (this.players.some((player) => sameCell(player.cell, cell) || sameCell(player.target, cell))) return false;
    if (this.enemies.some((enemy) => sameCell(enemy.cell, cell) || sameCell(enemy.target, cell))) return false;
    return !this.fruitActors.some((other) => other.id !== actor.id
      && (sameCell(other.cell, cell) || sameCell(other.from, cell) || sameCell(other.target, cell)));
  }

  private placeWall(player: PlayerState): void {
    const rules = this.wallRules();
    const preview = this.buildWallPreview(player, rules);
    const result = this.flipwalls.place(player.id, preview, this.tickCount, rules);
    if (!result.accepted || !result.line) {
      this.events.push({ type: 'wall-reject', playerId: player.id, label: result.reason });
      return;
    }
    this.resolveFruitUnderIce(result.line.cells);
    const firstCell = result.line.cells[0];
    this.events.push(firstCell
      ? { type: 'wall-place', playerId: player.id, cell: firstCell }
      : { type: 'wall-place', playerId: player.id });
    if (this.bossPhase === 2) {
      this.bossRedirects += 1;
      if (this.bossRedirects >= 3) this.setBossPhase(3);
      else this.triggerShift('ARENA SLIDE');
    }
  }

  /**
   * Arcade Frostwall rule: the action key builds a line in front of the cat,
   * and only melts ice when the cat is already facing a wall. Building a
   * second line therefore leaves the first standing — the previous behaviour
   * shattered everything the cat owned on the second press.
   */
  /**
   * Decides what happens to fruit standing where a Frostwall just grew.
   *
   * A fruit that can still move steps clear into a free neighbour, the way a
   * roaming berry dodges around an obstacle. Anything anchored, or boxed in
   * with nowhere to go, is sealed inside the ice: it holds still and cannot be
   * collected until a cat melts the block, which draws it as a silhouette.
   */
  private resolveFruitUnderIce(cells: readonly Cell[]): void {
    const covered = new Set(cells.map(cellKey));
    if (covered.size === 0) return;
    for (const actor of this.fruitActors) {
      if (actor.frozen) continue;
      const touches = covered.has(cellKey(actor.cell))
        || covered.has(cellKey(actor.from))
        || covered.has(cellKey(actor.target));
      if (!touches) continue;
      if (actor.behavior !== 'stationary' && this.dodgeFruitAside(actor, covered)) continue;
      this.freezeFruit(actor);
    }
  }

  /** Moves a fruit to a free neighbour, returning false when it is boxed in. */
  private dodgeFruitAside(actor: FruitActorState, covered: ReadonlySet<string>): boolean {
    const reachable = this.reachableFruitCells();
    const escapes = this.grid
      .neighbors(actor.cell, (cell) => !this.isSafeFruitDestination(cell, actor, reachable))
      .filter((cell) => !covered.has(cellKey(cell)));
    // Prefer carrying on in the direction it was already travelling.
    const ahead = stepCell(actor.cell, actor.facing);
    const destination = escapes.find((cell) => sameCell(cell, ahead))
      ?? escapes[this.fruitRandom.integer(Math.max(1, escapes.length))]
      ?? null;
    if (!destination) return false;
    // Reuse the normal relocation so the collectible registry follows the
    // fruit; moving it by hand would leave its old cell registered and make it
    // impossible to pick up. It glides aside rather than snapping.
    this.reserveFruitDestination(actor, destination);
    actor.phase = 'drifting';
    actor.phaseDurationTicks = FRUIT_DODGE_TICKS;
    actor.phaseTicksRemaining = FRUIT_DODGE_TICKS;
    actor.progress = 0;
    actor.collectible = true;
    this.events.push({ type: 'fruit-move', cell: destination, fruitKind: actor.kind, label: 'DODGE' });
    return true;
  }

  private freezeFruit(actor: FruitActorState): void {
    actor.cell = { ...actor.cell };
    actor.from = { ...actor.cell };
    actor.target = { ...actor.cell };
    actor.frozen = true;
    actor.collectible = false;
    actor.phase = 'frozen';
    actor.phaseTicksRemaining = 0;
    actor.phaseDurationTicks = 0;
    actor.progress = 0;
    this.fruitActionRetryTicks.delete(actor.id);
    this.events.push({ type: 'fruit-warning', cell: actor.cell, fruitKind: actor.kind, label: 'FROZEN SOLID' });
  }

  /** Frees any fruit whose ice has been melted or otherwise removed. */
  private thawFruitWithoutIce(): void {
    for (const actor of this.fruitActors) {
      if (!actor.frozen || this.flipwalls.hasCell(actor.cell)) continue;
      actor.frozen = false;
      actor.collectible = true;
      actor.phase = 'idle';
      actor.nextActionTick = this.tickCount + 12;
      this.events.push({ type: 'fruit-move', cell: actor.cell, fruitKind: actor.kind, label: 'THAWED' });
    }
  }

  private toggleWalls(player: PlayerState): void {
    const faced = stepCell(player.cell, player.facing);
    if (!this.flipwalls.hasCell(faced)) {
      this.placeWall(player);
      return;
    }
    // Only the run extending away from the cat melts. Facing along a line
    // clears all of it; facing into its side clears the single block in front.
    const run = this.flipwalls.runFrom(faced, player.facing);
    this.beginMelt(player, run);
  }

  /**
   * Schedules a melt: a short wind-up for the strike, then one block coming
   * apart every few steps outward from the cat.
   */
  private beginMelt(player: PlayerState, run: readonly Cell[]): void {
    if (run.length === 0) return;
    run.forEach((cell, index) => {
      const key = cellKey(cell);
      if (this.meltingCells.has(key)) return;
      this.meltingCells.set(key, this.tickCount + MELT_WINDUP_TICKS + index * MELT_TICKS_PER_CELL);
    });
    this.meltOwner = player.id;
    const struck = run[0];
    this.events.push(struck
      ? { type: 'wall-crack', playerId: player.id, cell: struck, label: 'FROST STRIKE' }
      : { type: 'wall-crack', playerId: player.id, label: 'FROST STRIKE' });
  }

  /** Removes each melting block as its moment arrives. */
  private advanceMelting(): void {
    if (this.meltingCells.size === 0) return;
    const due: Cell[] = [];
    for (const [key, tick] of this.meltingCells) {
      if (tick > this.tickCount) continue;
      const [xText, yText] = key.split(',');
      due.push({ x: Number(xText), y: Number(yText) });
      this.meltingCells.delete(key);
    }
    if (due.length === 0) return;
    const removed = this.flipwalls.clearCells(due);
    if (removed.length === 0) return;
    const owner = this.players.find((candidate) => candidate.id === this.meltOwner) ?? this.players[0];
    // Flagged as a melt so presentation knows the blow already landed on the
    // first block: the rest of the run comes apart without another jolt.
    if (owner) this.applyShatter(owner, removed, true);
  }

  private buildWallPreview(player: PlayerState, rules = this.wallRules()): Cell[] {
    return this.flipwalls.preview(
      player.cell,
      player.facing,
      rules,
      Math.max(this.level.width, this.level.height),
    );
  }

  /** Legacy dedicated shatter key: melts every wall this cat owns at once. */
  private shatterWalls(player: PlayerState): void {
    this.applyShatter(player, this.flipwalls.shatter(player.id).cells);
  }

  private applyShatter(player: PlayerState, cells: readonly Cell[], melting = false): void {
    if (cells.length === 0) return;
    let stuns = 0;
    const energized = this.worldState === 'overdrive';
    for (const enemy of this.enemies) {
      if (cells.some((cell) => manhattan(cell, enemy.cell) <= 2)) {
        enemy.stunnedTicks = energized ? 210 : 150;
        // A Frostwall burst knocks a charging Scoop out of its sprint and
        // leaves it winded, so shattering ice is a real answer to the charge.
        if (enemy.sprint) windLaneSprint(enemy.sprint);
        enemy.moving = false;
        enemy.cell = { ...enemy.from };
        enemy.target = { ...enemy.cell };
        enemy.progress = 0;
        stuns += 1;
        this.scoring.stunEnemy(energized);
        this.events.push({ type: 'enemy-stun', cell: enemy.cell, enemyKind: enemy.kind, amount: energized ? 2 : 1 });
      }
    }
    for (const breakable of [...this.level.breakables]) {
      const [xText, yText] = breakable.split(',');
      const cell = { x: Number(xText), y: Number(yText) };
      if (cells.some((wall) => manhattan(wall, cell) <= 1)) {
        this.level.breakables.delete(breakable);
        this.topologyRevision += 1;
      }
    }
    if (this.bossPhase === 3 && energized) {
      this.bossOverloads += 1;
      if (this.bossOverloads >= 3) this.setBossPhase(4);
    }
    this.events.push({
      type: 'wall-shatter',
      playerId: player.id,
      amount: stuns,
      label: melting ? 'MELT' : energized ? 'ENERGIZED' : 'PULSE',
    });
  }

  private useSpecial(player: PlayerState): void {
    if (player.specialCooldownTicks > 0) return;
    if (this.level.shiftPads.has(cellKey(player.cell))) {
      this.triggerShift('SHIFT PAD');
      player.specialCooldownTicks = 120;
      this.events.push({ type: 'special', playerId: player.id, label: 'SHIFT PAD' });
      return;
    }
    if (player.character === 'mox') {
      const destination = stepCell(player.cell, player.facing);
      if (!this.isBlockedForPlayer(destination) && !this.isPlayerReserved(destination, player.id)) {
        player.cell = { ...destination };
        player.from = { ...destination };
        player.target = { ...destination };
        player.moving = false;
        this.onPlayerArrived(player);
      }
      player.specialCooldownTicks = 210;
      this.events.push({ type: 'special', playerId: player.id, cell: destination, label: getFlavor(player.flavor).specialName.toUpperCase() });
    } else {
      player.decoy = { cell: { ...player.cell }, direction: player.facing, remainingTicks: 300, moveTicks: 0 };
      player.specialCooldownTicks = 360;
      this.events.push({ type: 'special', playerId: player.id, cell: player.cell, label: getFlavor(player.flavor).specialName.toUpperCase() });
    }
  }

  private updateDecoy(player: PlayerState): void {
    const decoy = player.decoy;
    if (!decoy) return;
    decoy.remainingTicks -= 1;
    decoy.moveTicks += 1;
    if (decoy.moveTicks >= 30) {
      decoy.moveTicks = 0;
      const destination = stepCell(decoy.cell, decoy.direction);
      if (!this.isStaticBlocked(destination, this.worldState)) decoy.cell = { ...destination };
      else decoy.direction = this.random.integer(2) === 0 ? 'left' : 'right';
    }
    if (decoy.remainingTicks <= 0) player.decoy = null;
  }

  private updateEnemy(enemy: EnemyState): void {
    // Lane-charge timers run on every fixed step so a charge cannot be paused
    // by standing mid-tile, but the phase itself only flips between tiles (see
    // `advanceLaneSprint`) so an enemy never changes speed halfway across one.
    if (enemy.sprint) tickLaneSprint(enemy.sprint);
    if (enemy.stunnedTicks > 0) {
      enemy.stunnedTicks -= 1;
      return;
    }
    if (enemy.alertTicks > 0) {
      enemy.alertTicks -= 1;
      if (enemy.alertTicks === 35) this.events.push({ type: 'enemy-alert', cell: enemy.cell, enemyKind: enemy.kind, label: ENEMY_LABELS[enemy.kind] });
      return;
    }
    if (enemy.warningTicks > 0) {
      enemy.warningTicks -= 1;
      return;
    }
    if (enemy.moving) {
      const ticks = enemyMoveTicks(enemy, this.worldState, this.difficulty, this.extractionStartTick !== null, (cell) => this.flipwalls.hasCell(cell));
      enemy.progress += 1 / ticks;
      if (enemy.progress >= 1) {
        enemy.cell = { ...enemy.target };
        enemy.from = { ...enemy.cell };
        enemy.progress = 0;
        enemy.moving = false;
        this.checkPlayerCollisions(enemy);
      }
      return;
    }

    const focus = nearestPlayer(enemy, this.players);
    if (!focus) return;
    if (enemy.sprint && this.advanceLaneSprint(enemy)) return;
    const decoyOwner = this.players.find((player) => player.decoy && (enemy.kind === 'tracer' || enemy.kind === 'seer'));
    let goal: Cell = decoyOwner?.decoy?.cell ?? focus.cell;
    if (enemy.kind === 'seer') {
      goal = predictIntercept(focus, (cell) => this.isBlockedForEnemy(cell) || this.isRoomBlockedForEnemy(enemy, cell));
      if (!enemy.predicted || !sameCell(enemy.predicted, goal)) {
        enemy.predicted = { ...goal };
        enemy.warningTicks = 20;
        return;
      }
    }
    const routeChanged = enemy.topologyRevision !== this.topologyRevision
      || !enemy.pathGoal
      || !sameCell(enemy.pathGoal, goal);
    if (routeChanged) {
      enemy.path = findPath(
        this.grid,
        enemy.cell,
        goal,
        (cell) => this.isBlockedForEnemy(cell) || this.isRoomBlockedForEnemy(enemy, cell),
        this.grid.width * this.grid.height,
      );
      enemy.pathGoal = { ...goal };
      enemy.topologyRevision = this.topologyRevision;
    }
    const next = enemy.path.shift();
    if (!next) {
      this.roamEnemy(enemy, goal);
      return;
    }
    if (this.isRoomBlockedForEnemy(enemy, next)) {
      enemy.path = [];
      enemy.pathGoal = null;
      return;
    }
    if (enemy.kind === 'wisp' && this.worldState === 'overdrive' && this.flipwalls.hasCell(next)) {
      enemy.warningTicks = 12;
      this.events.push({ type: 'enemy-alert', cell: enemy.cell, enemyKind: enemy.kind, label: 'RAIL SURGE' });
    }
    enemy.from = { ...enemy.cell };
    enemy.target = { ...next };
    enemy.facing = directionBetween(enemy.cell, next);
    enemy.progress = 0;
    enemy.moving = true;
  }

  /**
   * Cherry Scoop lane charge, resolved only between tiles.
   *
   * Returns true when the sprint took the turn: either the Scoop is holding
   * still through its lock-on telegraph, or it committed a straight dash down
   * the lane. Returning false hands the turn back to the shared A* pursuit,
   * which still runs at sprint speed while the charge lasts.
   */
  private advanceLaneSprint(enemy: EnemyState): boolean {
    const sprint = enemy.sprint;
    if (!sprint) return false;
    if (sprint.ticksRemaining === 0) {
      if (sprint.phase === 'locking') {
        beginLaneSprint(sprint);
      } else if (sprint.phase === 'sprinting') {
        windLaneSprint(sprint);
        this.events.push({ type: 'enemy-alert', cell: enemy.cell, enemyKind: enemy.kind, amount: 0, label: 'SCOOP OUT OF BREATH' });
      } else if (sprint.phase === 'winded') {
        readyLaneSprint(sprint);
      }
    }

    const targets = this.players.filter((player) => !player.downed).map((player) => player.cell);
    const sighting = firstLaneSighting(
      enemy.cell,
      targets,
      (cell) => this.isBlockedForEnemy(cell) || this.isRoomBlockedForEnemy(enemy, cell),
    );

    if (sprint.phase === 'ready') {
      if (!sighting) return false;
      beginLaneLock(sprint, sighting);
      enemy.facing = sighting;
      this.events.push({ type: 'enemy-alert', cell: enemy.cell, enemyKind: enemy.kind, amount: 1, label: 'SCOOP LOCKED ON!' });
      return true;
    }
    // Wind-up: rooted in place, facing the lane it is about to run down.
    if (sprint.phase === 'locking') return true;
    if (sprint.phase !== 'sprinting') return false;

    // While the lane holds, barrel straight down it. Once the cat breaks the
    // line the Scoop keeps its speed but falls back to normal pursuit, and it
    // re-commits the moment the lane lines up again.
    sprint.lane = sighting;
    if (!sighting) return false;
    const next = stepCell(enemy.cell, sighting);
    if (this.isBlockedForEnemy(next) || this.isRoomBlockedForEnemy(enemy, next) || this.isEnemyOccupied(enemy, next)) return false;
    this.commitEnemyStep(enemy, next);
    return true;
  }

  private isEnemyOccupied(enemy: EnemyState, cell: Cell): boolean {
    return this.enemies.some((other) => other.id !== enemy.id && (sameCell(other.cell, cell) || sameCell(other.target, cell)));
  }

  private commitEnemyStep(enemy: EnemyState, next: Cell): void {
    enemy.from = { ...enemy.cell };
    enemy.target = { ...next };
    enemy.facing = directionBetween(enemy.cell, next);
    enemy.progress = 0;
    enemy.moving = true;
    enemy.path = [];
    enemy.pathGoal = null;
  }

  /**
   * Keeps a hunter alive when Frostwalls make its target unreachable. It
   * wanders through the area it can still reach, generally continuing forward
   * and avoiding an immediate U-turn when another direction is open. The
   * seeded choice at junctions makes the patrol feel loose while preserving
   * deterministic simulation and replays.
   */
  private roamEnemy(enemy: EnemyState, unreachableGoal: Cell): boolean {
    const candidates = this.grid.neighbors(
      enemy.cell,
      (cell) => this.isBlockedForEnemy(cell)
        || this.isRoomBlockedForEnemy(enemy, cell)
        || this.isEnemyOccupied(enemy, cell),
    );
    if (candidates.length === 0) return false;

    const reverseCell = stepCell(enemy.cell, OPPOSITE_DIRECTION[enemy.facing]);
    const forwardCell = stepCell(enemy.cell, enemy.facing);
    const withoutReverse = candidates.filter((cell) => !sameCell(cell, reverseCell));
    const choices = withoutReverse.length > 0 ? withoutReverse : candidates;
    const forward = choices.find((cell) => sameCell(cell, forwardCell));
    const next = forward && this.enemyRandom.integer(4) !== 0
      ? forward
      : choices[this.enemyRandom.integer(choices.length)];
    if (!next) return false;
    this.commitEnemyStep(enemy, next);
    // Every patrol step remains in the same connected component. Retain the
    // failed goal so A* is retried only when the cat or topology changes.
    enemy.pathGoal = { ...unreachableGoal };
    enemy.topologyRevision = this.topologyRevision;
    return true;
  }

  private updateHazards(): void {
    for (const player of this.players) {
      if (player.downed) continue;
      const key = cellKey(player.cell);
      if ((this.level.lasers.has(key) && this.isLaserActive()) || (this.level.fadingFloors.has(key) && this.isFadingFloorActive())) {
        this.damagePlayer(player, 'HAZARD');
      }
    }
  }

  private updateBoss(): void {
    if (this.bossPhase === 0 || this.bossPhase === 4) return;
    this.bossPulseTick += 1;
    const cycle = this.bossPulseTick % 240;
    if (cycle === 185) {
      this.events.push(this.level.boss
        ? { type: 'enemy-alert', cell: this.level.boss, label: 'SHOCKWAVE' }
        : { type: 'enemy-alert', label: 'SHOCKWAVE' });
    }
    if (cycle === 220) {
      const boss = this.level.boss;
      if (!boss) return;
      for (const player of this.players) {
        const distance = manhattan(player.cell, boss);
        if (distance >= 3 && distance <= 4) this.damagePlayer(player, 'SHOCKWAVE');
      }
    }
  }

  private updateShiftSchedule(): void {
    if (this.nextShiftTick === null) return;
    const remaining = this.nextShiftTick - this.tickCount;
    if (remaining <= 180 && remaining > 0 && !this.shiftWarningActive) {
      this.shiftWarningActive = true;
      this.events.push({ type: 'shift-warning', amount: remaining, label: this.worldState === 'normal' ? 'OVERDRIVE INCOMING' : 'NORMALIZING' });
    }
    if (remaining <= 0) this.triggerShift('AUTOMATIC SHIFT');
  }

  private updateRevives(): void {
    for (const downed of this.players.filter((player) => player.downed)) {
      const rescuer = this.players.find((player) => !player.downed && manhattan(player.cell, downed.cell) <= 1);
      downed.reviveTicks = rescuer ? downed.reviveTicks + 1 : Math.max(0, downed.reviveTicks - 2);
      if (downed.reviveTicks >= 72) {
        downed.downed = false;
        downed.health = 1;
        downed.reviveTicks = 0;
        downed.invulnerableTicks = 120;
        this.events.push({ type: 'revive', playerId: downed.id, cell: downed.cell });
      }
    }
  }

  private checkEnemyCollision(player: PlayerState): void {
    for (const enemy of this.enemies) {
      if (enemy.stunnedTicks === 0 && (sameCell(enemy.cell, player.cell) || sameCell(enemy.target, player.cell) && enemy.progress > 0.7)) {
        this.damagePlayer(player, enemy.kind.toUpperCase());
      }
    }
  }

  private checkPlayerCollisions(enemy: EnemyState): void {
    for (const player of this.players) {
      if (!player.downed && sameCell(player.cell, enemy.cell)) this.damagePlayer(player, enemy.kind.toUpperCase());
    }
  }

  private damagePlayer(player: PlayerState, source: string): void {
    if (this.invulnerable || player.invulnerableTicks > 0 || player.downed) return;
    player.health = 0;
    player.invulnerableTicks = 90;
    this.scoring.damage();
    this.events.push({ type: 'damage', playerId: player.id, cell: player.cell, label: source });
    player.downed = true;
    player.moving = false;
    player.buffered = null;
    player.bufferTicks = 0;
    player.wallToggleQueued = false;
    player.wallHeld = false;
    this.events.push({ type: 'thaw', playerId: player.id, cell: player.cell, label: 'FROST SHELL RELEASED' });
    if (this.players.every((candidate) => candidate.downed)) {
      this.failed = true;
      this.events.push({ type: 'failure', label: this.players.length === 1 ? 'ICECAT THAWED' : 'TEAM THAWED' });
    }
  }

  private onAllCoresCollected(): void {
    if (this.definition.boss && this.bossPhase === 1) {
      this.setBossPhase(2);
      return;
    }
    this.openPortal();
  }

  private setBossPhase(phase: 2 | 3 | 4): void {
    this.bossPhase = phase;
    this.events.push({ type: 'boss-phase', amount: phase, label: phase === 2 ? 'REDIRECT' : phase === 3 ? 'OVERLOAD' : 'BREACH' });
    if (phase === 2) {
      // Start the redirect puzzle with fresh construction capacity. This is a
      // neutral dissolve, not a shatter: it cannot stun or award score.
      this.flipwalls.clear();
      this.syncFlipwallTopology();
      if (this.worldState !== 'overdrive') this.triggerShift('WARDEN RAILS');
      this.nextShiftTick = this.tickCount + 720;
    }
    if (phase === 3) {
      this.nextShiftTick = this.tickCount + 360;
      this.shiftWarningActive = false;
    }
    if (phase === 4) this.openPortal();
  }

  private openPortal(): void {
    if (this.portalOpen) return;
    this.portalOpen = true;
    this.extractionStartTick = this.tickCount;
    if (this.worldState !== 'overdrive') this.triggerShift('EXTRACTION OVERDRIVE');
    this.events.push({ type: 'portal-open', cell: this.level.exit });
  }

  private checkExtractionTimer(): void {
    if (this.extractionStartTick === null) return;
    this.scoring.state.extractionTicks += 1;
    const limit = Math.round(this.definition.extractionMs / (1000 / TICKS_PER_SECOND));
    if (this.tickCount - this.extractionStartTick <= limit) return;
    this.failed = true;
    this.events.push({ type: 'failure', label: 'SNOWGLOBE COLLAPSED' });
  }

  private finishRun(): void {
    if (this.finished) return;
    this.finished = true;
    this.result = this.scoring.finish(this.definition, this.tickCount);
    this.events.push({ type: 'victory', cell: this.level.exit, amount: this.result.score });
  }

  private wallRules(): FlipwallRules {
    return {
      isInvalid: (cell) => !this.grid.isInside(cell)
        || this.isStaticBlocked(cell, this.worldState)
        || this.level.gates.has(cellKey(cell))
        || this.level.lasers.has(cellKey(cell))
        || this.level.fadingFloors.has(cellKey(cell))
        // Fruit no longer blocks a Frostwall: ice built over a fruit either
        // shoves it aside or seals it in, resolved in `resolveFruitUnderIce`.
        || sameCell(cell, this.level.exit)
        || (this.shardAvailable !== null && sameCell(cell, this.shardAvailable))
        || (this.level.boss !== null && sameCell(cell, this.level.boss)),
      isOccupied: (cell) => this.players.some((candidate) => sameCell(candidate.cell, cell) || sameCell(candidate.target, cell))
        || this.players.some((candidate) => candidate.decoy !== null && sameCell(candidate.decoy.cell, cell))
        || this.enemies.some((enemy) => sameCell(enemy.cell, cell) || sameCell(enemy.target, cell)),
      // A cat may wall off any route, including the last one into a pocket.
      // Frostwalls are melted on demand by facing them, so ice can never
      // strand an objective the way the old temporary, one-line walls could.
      isSafe: () => true,
    };
  }

  /**
   * Starts the run from a later room. Every earlier room counts as already
   * harvested, which is what opens the doors behind the cat and lets it walk
   * back through the rooms it has cleared.
   */
  enterRoom(index: number): void {
    if (!this.world || index <= 0) return;
    const room = this.world.rooms[Math.min(index, this.world.rooms.length - 1)];
    if (!room) return;
    while (this.activeFruitWaveIndex < room.index && !this.isHarvestComplete()) {
      this.completeActiveFruitWave();
    }
    for (const player of this.players) {
      player.cell = { ...room.spawn };
      player.from = { ...room.spawn };
      player.target = { ...room.spawn };
      player.progress = 0;
      player.moving = false;
    }
  }

  /** Which room a cell sits in; always 0 outside the connected world. */
  roomOf(cell: Cell): number {
    return this.world ? roomIndexAt(cell, this.roomCount) : 0;
  }

  /**
   * A doorway stays sealed until the room behind it has been harvested, which
   * is exactly the moment its wave is cleared and the next one begins.
   */
  isDoorSealed(cell: Cell): boolean {
    if (!this.world) return false;
    const door = this.world.doors.find((candidate) => sameCell(candidate.cell, cell));
    if (!door) return false;
    return this.activeFruitWaveIndex <= door.room;
  }

  /** The room a hunter may not pass, and the reason it can be herded backward. */
  private isRoomBlockedForEnemy(enemy: EnemyState, cell: Cell): boolean {
    return this.roomOf(cell) > enemy.homeRoom;
  }

  private isStaticBlocked(cell: Cell, state: WorldState): boolean {
    if (!this.grid.isInside(cell)) return true;
    if (this.isDoorSealed(cell)) return true;
    const key = cellKey(cell);
    if (this.level.permanentWalls.has(key) || this.level.breakables.has(key)) return true;
    if (this.level.gates.has(key) && this.isGateClosed()) return true;
    const stateWall = state === 'normal' ? this.level.normalWalls.has(key) : this.level.overdriveWalls.has(key);
    if (!stateWall) return false;
    return !this.players.some((player) => sameCell(player.cell, cell) || sameCell(player.target, cell));
  }

  private isBlockedForPlayer(cell: Cell): boolean {
    return this.isStaticBlocked(cell, this.worldState) || (this.worldState === 'normal' && this.flipwalls.hasCell(cell));
  }

  private isBlockedForEnemy(cell: Cell): boolean {
    return this.isStaticBlocked(cell, this.worldState) || (this.worldState === 'normal' && this.flipwalls.hasCell(cell));
  }

  private isPlayerReserved(cell: Cell, except: 1 | 2): boolean {
    return this.players.some((player) => player.id !== except && !player.downed && (sameCell(player.cell, cell) || sameCell(player.target, cell)));
  }

  private updateCooldowns(): void {
    for (const id of [1, 2] as const) {
      const value = this.nearMissCooldown.get(id) ?? 0;
      if (value > 0) this.nearMissCooldown.set(id, value - 1);
    }
  }

  private syncFlipwallTopology(): void {
    if (this.previousFlipwallRevision === this.flipwalls.revision) return;
    this.previousFlipwallRevision = this.flipwalls.revision;
    this.topologyRevision += 1;
  }
}
